import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Brain, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

interface AIProgressModalProps {
  isOpen: boolean;
  onComplete: (result: { success: boolean; pdfBase64?: string; error?: string; processedItems?: any[] }) => void;
  items: any[];
  header: any;
  gewerkOrder: string[];
  aiMode: 'light' | 'max';
  options?: any;
}

interface ProgressUpdate {
  type: 'start' | 'progress' | 'complete' | 'error';
  total?: number;
  current?: number;
  position?: string;
  status?: 'processing' | 'done' | 'skipped' | 'error';
  message?: string;
  originalLength?: number;
  newLength?: number;
  pdfBase64?: string;
  pdfSize?: number;
}

export function AIProgressModal({
  isOpen,
  onComplete,
  items,
  header,
  gewerkOrder,
  aiMode,
  options
}: AIProgressModalProps) {
  const [progress, setProgress] = useState(0);
  const [currentPosition, setCurrentPosition] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState<'connecting' | 'processing' | 'complete' | 'error'>('connecting');
  const [errorMessage, setErrorMessage] = useState('');
  const [stats, setStats] = useState({ optimized: 0, skipped: 0, errors: 0 });

  useEffect(() => {
    if (!isOpen) return;

    // Reset state
    setProgress(0);
    setProcessedCount(0);
    setStatus('connecting');
    setStats({ optimized: 0, skipped: 0, errors: 0 });

    const controller = new AbortController();

    const startProcessing = async () => {
      try {
        // Use EventSource for SSE
        const response = await fetch('/api/generate-pdf-with-progress', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({
            header,
            items,
            gewerkOrder,
            options: {
              ...options,
              aiOptimization: aiMode,
            }
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        // Check if it's SSE or regular response
        const contentType = response.headers.get('content-type');

        if (contentType?.includes('text/event-stream')) {
          // SSE response - process stream
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) throw new Error('No response body');

          setStatus('processing');
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();

            if (value) {
              buffer += decoder.decode(value, { stream: !done });
            }

            // Process all complete lines in buffer
            const lines = buffer.split('\n');

            // When done, process ALL lines including the last one (might not have trailing newline)
            // When not done, keep the last incomplete line in the buffer
            if (done) {
              // Process all lines when stream ends
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data: ProgressUpdate = JSON.parse(line.slice(6));
                    console.log('📨 SSE event (final):', data.type);
                    handleProgressUpdate(data);
                  } catch (e) {
                    console.warn('SSE parse error (final):', e, line);
                  }
                }
              }
              console.log('🏁 SSE stream ended');
              break;
            } else {
              // Keep last incomplete line in buffer
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data: ProgressUpdate = JSON.parse(line.slice(6));
                    handleProgressUpdate(data);
                  } catch (e) {
                    console.warn('SSE parse error:', e);
                  }
                }
              }
            }
          }
        } else if (contentType?.includes('application/pdf')) {
          // Direct PDF response (no streaming)
          setStatus('processing');
          setTotalCount(items.length);

          // Simulate progress for non-streaming response
          const blob = await response.blob();

          setStatus('complete');
          setProgress(100);

          onComplete({
            success: true,
            processedItems: items, // Original items, server processed them
          });

          // Return the blob via a custom event
          window.dispatchEvent(new CustomEvent('pdf-ready', { detail: { blob } }));
        }

      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return; // Ignore abort errors
        }
        const errMsg = error instanceof Error ? error.message : 'Unbekannter Fehler';
        setStatus('error');
        setErrorMessage(errMsg);
        onComplete({ success: false, error: errMsg });
      }
    };

    const handleProgressUpdate = (data: ProgressUpdate) => {
      switch (data.type) {
        case 'start':
          setTotalCount(data.total || items.length);
          setStatus('processing');
          break;

        case 'progress':
          setProcessedCount(data.current || 0);
          setCurrentPosition(data.position || '');
          setProgress(((data.current || 0) / (data.total || 1)) * 100);

          if (data.status === 'done') {
            setStats(prev => ({ ...prev, optimized: prev.optimized + 1 }));
          } else if (data.status === 'skipped') {
            setStats(prev => ({ ...prev, skipped: prev.skipped + 1 }));
          } else if (data.status === 'error') {
            setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
          }
          break;

        case 'complete':
          console.log('✅ SSE complete event received, PDF size:', data.pdfSize);
          setStatus('complete');
          setProgress(100);
          // Call onComplete with PDF data
          if (data.pdfBase64) {
            setTimeout(() => {
              onComplete({ success: true, pdfBase64: data.pdfBase64 });
            }, 500); // Small delay to show completion state
          } else {
            console.warn('⚠️ Complete event without pdfBase64');
            onComplete({ success: false, error: 'PDF-Daten fehlen' });
          }
          break;

        case 'error':
          setStatus('error');
          setErrorMessage(data.message || 'Verarbeitung fehlgeschlagen');
          setTimeout(() => {
            onComplete({ success: false, error: data.message || 'Verarbeitung fehlgeschlagen' });
          }, 1000);
          break;
      }
    };

    startProcessing();

    return () => {
      controller.abort();
    };
  }, [isOpen, items, header, gewerkOrder, aiMode, options, onComplete]);

  const modeLabel = aiMode === 'max' ? 'MAX Restrukturierung' : 'Leichte Optimierung';
  const modeColor = aiMode === 'max' ? 'bg-orange-500' : 'bg-blue-500';

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            KI-Optimierung
            <Badge className={`${modeColor} text-white ml-2`}>{modeLabel}</Badge>
          </DialogTitle>
          <DialogDescription>
            Texte werden mit KI optimiert. Bitte warten...
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-6">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fortschritt</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          {/* Status */}
          <div className="flex items-center justify-center gap-3">
            {status === 'connecting' && (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                <span className="text-sm">Verbinde mit KI-Service...</span>
              </>
            )}
            {status === 'processing' && (
              <>
                <Sparkles className="h-5 w-5 text-purple-600 animate-pulse" />
                <span className="text-sm">
                  Position {processedCount} von {totalCount || items.length}
                </span>
              </>
            )}
            {status === 'complete' && (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-600">Optimierung abgeschlossen</span>
              </>
            )}
            {status === 'error' && (
              <>
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-600">{errorMessage}</span>
              </>
            )}
          </div>

          {/* Current Position */}
          {status === 'processing' && currentPosition && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Aktuell:</p>
              <p className="text-sm font-medium truncate max-w-[300px] mx-auto">
                {currentPosition}
              </p>
            </div>
          )}

          {/* Stats */}
          {(stats.optimized > 0 || stats.skipped > 0 || stats.errors > 0) && (
            <div className="flex justify-center gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span>{stats.optimized} optimiert</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                <span>{stats.skipped} übersprungen</span>
              </div>
              {stats.errors > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>{stats.errors} Fehler</span>
                </div>
              )}
            </div>
          )}

          {/* Animation */}
          {status === 'processing' && (
            <div className="flex justify-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AIProgressModal;
